import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feeRuleCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { FeeRuleDto } from "@/features/fee-engine/api/dto";

/**
 * Active une règle de frais (`POST /FeeRules/{id}/activate`) — action
 * SENSIBLE (guide §6.5) : archive automatiquement la règle active
 * précédente du même périmètre.
 *
 * Action protégée maker-checker (guide §5.4) : exige
 * `?approvalRequestId=...` d'une demande FEE_RULE_CHANGE APPROVED ciblant
 * cette règle (404 si inconnue, 422 sinon). Ce hook est l'EXÉCUTEUR appelé
 * par la page détail d'une ApprovalRequest (`routes/admin/`) après
 * approbation ; la création de la demande passe par
 * `useSubmitApprovalRequest` (`@/features/shared`).
 */
export const useActivateFeeRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "fee-rules", "activate"],
    mutationFn: ({
      feeRuleId,
      approvalRequestId,
    }: {
      feeRuleId: string;
      approvalRequestId: string;
    }) =>
      mutateData({
        url: `${feeEngineEndpoints.feeRules.activate(
          feeRuleId
        )}?approvalRequestId=${encodeURIComponent(approvalRequestId)}`,
        method: "POST",
      }) as Promise<FeeRuleDto>,
    onSuccess: (_data, { feeRuleId }) => {
      return invalidateAfterMutation(queryClient, feeRuleCacheKeys(feeRuleId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useActivateFeeRule failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
